// src/lib/map-overlay/CaptureChips.js
//
// The selected capture's chip (footprint restyle S-c2, DB 2026-11-03): a navy
// DOM label, "7 Jun 2026 · 10 cm · zoom in", shown while the map is below the
// capture's minzoom (the T3 edge's zooms, S-c1) and hidden at and above it.
// DOM, not a symbol layer: the SitePlanner style has no glyphs. Built on
// screenLabels.js: its cull keeps the chips apart and its pool holds them.
//
//   WHERE     chipPlacement.js: label_point, else top-center when the
//             footprint fills the view, else the visible footprint nearest the
//             center, else hidden. The safe area is the map minus what floats
//             on it (chipObstacles), and in Compare minus the other side
//             (clipX).
//   WHEN      placed on moveend, never during a pan or zoom: a chip holds its
//             screen position through a gesture, and keeps it after while it
//             is still in the safe area and on the footprint. Also placed on
//             new data, a resize, a clipX change (place()), and when
//             something it avoids changes size (a ResizeObserver on them:
//             the strip grows a row after a move). A zoom that reaches the
//             minzoom hides the chip at once; it shows again only on moveend.
//   HOW MANY  one per selected capture, topmost first; two that would touch
//             keep the topmost; at most CHIP_MAX.
//
// A map removed under it (Explore's compare map, REM-703) is never called:
// each entry point asks isLiveMap first.
//
// The overlay sits in the map's own container, so Compare's clip of each map
// clips that map's chips with it.
import { cullLabels, LabelPool } from "./screenLabels.js";
import { chipRegion, placeChip } from "./chipPlacement.js";
import { isLiveMap } from "../map/liveMap.js";
import { NAVY } from "../map/footprintStyle.js";
import { formatImageryDate, formatResolution, UNDATED } from "../../padplanner/planVocabulary.js";

export const CHIP_MAX = 3;
const CELL = Object.freeze({ w: 16, h: 16 });

// What the chip avoids, in the frame around the map (data-chip-frame):
// anything marked data-chip-avoid, MapLibre's control corners, Compare's
// slider. A MapHud slot counts (as its whole box) only with an item drawn.
const AVOID = "[data-chip-avoid], .maplibregl-ctrl-top-left, .maplibregl-ctrl-top-right, "
  + ".maplibregl-ctrl-bottom-left, .maplibregl-ctrl-bottom-right, .compare-swiper-vertical";

/** "7 Jun 2026 · 10 cm · zoom in", from an /imagery/entitled feature's properties. */
export function chipText(p) {
  return [formatImageryDate(p?.acquisition_date) ?? UNDATED, formatResolution(p?.resolution_cm), "zoom in"]
    .filter(Boolean).join(" · ");
}

/**
 * The chips for the selected captures: one per key with an entitled feature,
 * in the keys' order (topmost first). A feature with no minzoom has no zoom
 * to ask for, and no chip.
 */
export function captureChipItems(features, keys) {
  const byKey = new Map((features ?? []).map((f) => [f?.properties?.dataset_key, f]));
  const out = [];
  for (const key of keys ?? []) {
    const f = byKey.get(key);
    const p = f?.properties;
    if (!p || !Number.isFinite(Number(p.minzoom)) || p.minzoom === null) continue;
    const g = f.geometry;
    const rings = g?.type === "Polygon" ? g.coordinates : g?.type === "MultiPolygon" ? g.coordinates.flat() : null;
    if (!rings?.length) continue;
    out.push({ key, text: chipText(p), minzoom: Number(p.minzoom), rings, labelPoint: p.label_point ?? null });
  }
  return out;
}

const shown = (el) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

/** The boxes the chips avoid, in `container`'s pixels. */
export function chipObstacles(frame, container) {
  if (!frame) return [];
  const c = container.getBoundingClientRect();
  const rel = (r) => ({ x0: r.left - c.left, y0: r.top - c.top, x1: r.right - c.left, y1: r.bottom - c.top });
  const out = [];
  for (const slot of frame.querySelectorAll("[data-hud-slot]")) {
    if ([...slot.querySelectorAll(":scope > [data-hud-item]")].some(shown)) out.push(rel(slot.getBoundingClientRect()));
  }
  for (const el of frame.querySelectorAll(AVOID)) if (shown(el)) out.push(rel(el.getBoundingClientRect()));
  return out;
}

function makeChip() {
  const el = document.createElement("div");
  el.dataset.testid = "capture-chip";
  Object.assign(el.style, {
    position: "absolute", left: "0", top: "0", pointerEvents: "none", whiteSpace: "nowrap",
    font: "600 12px/16px ui-sans-serif, system-ui, sans-serif", color: "#ffffff", background: NAVY,
    border: "1px solid rgba(255, 255, 255, 0.35)", borderRadius: "4px", padding: "3px 8px",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.45)",
  });
  return el;
}

export default class CaptureChips {
  /**
   * @param {object} o
   * @param {object} o.map                      a MapLibre map
   * @param {() => [number, number]|null} [o.clipX]  the visible span (Compare)
   * @param {(el: HTMLElement) => {w: number, h: number}} [o.measure]  a chip's size
   */
  constructor({ map, clipX = () => null, measure = null }) {
    this.map = map;
    this.clipX = clipX;
    this.measure = measure ?? ((el) => ({ w: el.offsetWidth, h: el.offsetHeight }));
    this.items = [];
    this.visible = true;
    this.current = new Map();
    this.placed = [];
    const container = map.getContainer();
    this.container = container;
    this.frame = container.closest?.("[data-chip-frame]") ?? container.parentElement;
    const el = document.createElement("div");
    el.dataset.testid = "capture-chips";
    Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden", zIndex: "2" });
    container.appendChild(el);
    this.el = el;
    // A hidden sizer: a chip's text measured without showing it.
    this.sizer = makeChip();
    this.sizer.dataset.testid = "capture-chip-sizer";
    this.sizer.setAttribute("aria-hidden", "true");
    Object.assign(this.sizer.style, { visibility: "hidden" });
    el.appendChild(this.sizer);
    this.pool = new LabelPool(el, { make: makeChip, size: CHIP_MAX });
    this._onMoveEnd = () => this.place();
    this._onZoom = () => this._gate();
    map.on("moveend", this._onMoveEnd);
    map.on("resize", this._onMoveEnd);
    map.on("zoom", this._onZoom);
    // Not during a gesture: the readout's zoom text alone resizes on every frame of one.
    this.observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => { if (!this.map.isMoving?.()) this.place(); }) : null;
    this.watched = new Set();
  }

  /** Off the map; a removed map (REM-703) is not called. */
  destroy() {
    if (isLiveMap(this.map)) {
      this.map.off("moveend", this._onMoveEnd);
      this.map.off("resize", this._onMoveEnd);
      this.map.off("zoom", this._onZoom);
    }
    this.observer?.disconnect();
    this.el.remove();
  }

  /** items: captureChipItems's, topmost first. */
  set(items, { visible = true } = {}) {
    this.items = items ?? [];
    this.visible = visible;
    for (const k of [...this.current.keys()]) if (!this.items.some((i) => i.key === k)) this.current.delete(k);
    this.place();
  }

  _size(text) {
    this.sizer.textContent = text;
    const s = this.measure(this.sizer);
    return s.w > 0 && s.h > 0 ? s : { w: Math.ceil(text.length * 7 + 18), h: 24 };
  }

  _watch() {
    if (!this.observer || !this.frame) return;
    const els = [...this.frame.querySelectorAll(`[data-hud-slot], ${AVOID}`)];
    for (const e of els) if (!this.watched.has(e)) { this.observer.observe(e); this.watched.add(e); }
  }

  /** Place every chip now: on moveend, new data, a resize, a clipX change. */
  place() {
    if (!isLiveMap(this.map)) return;
    const zoom = this.map.getZoom();
    const todo = this.visible ? this.items.filter((i) => zoom < i.minzoom) : [];
    if (!todo.length) { this.current.clear(); this.placed = []; this.pool.render([], () => {}); return; }
    const width = this.container.clientWidth, height = this.container.clientHeight;
    const obstacles = chipObstacles(this.frame, this.container);
    this._watch();
    const c = this.map.getCenter();
    const center = this._xy([c.lng, c.lat]);
    const out = [];
    for (const item of todo) {
      const chip = this._size(item.text);
      const region = chipRegion({ width, height, chip, obstacles, clipX: this.clipX() });
      const rings = item.rings.map((r) => r.map((p) => this._xy(p)));
      const now = this.current.get(item.key) ?? null;
      const p = placeChip({ rings, labelPoint: item.labelPoint ? this._xy(item.labelPoint) : null, center, region, current: now });
      // A kept chip still says which rule put it there.
      if (p) out.push({ item, chip, x: p.x, y: p.y, branch: p.branch === "kept" ? now.branch : p.branch, kept: p.branch === "kept" });
    }
    const box = out.reduce((b, o) => ({ w: Math.max(b.w, o.chip.w), h: Math.max(b.h, o.chip.h) }), { w: 0, h: 0 });
    const kept = cullLabels(out, {
      project: ([x, y]) => ({ x, y }), at: (o) => [o.x, o.y], width, height, cell: CELL, max: CHIP_MAX,
      box: { dx: -box.w / 2, dy: -box.h / 2, w: box.w, h: box.h },
    }).map((k) => k.item);
    this.current = new Map(kept.map((o) => [o.item.key, { x: o.x, y: o.y, branch: o.branch }]));
    this.placed = kept;
    this._render(kept);
  }

  // A zoom past a chip's minzoom hides it now; nothing moves.
  _gate() {
    if (!isLiveMap(this.map)) return;
    const zoom = this.map.getZoom();
    const left = this.placed.filter((o) => zoom < o.item.minzoom);
    if (left.length === this.placed.length) return;
    for (const o of this.placed) if (!left.includes(o)) this.current.delete(o.item.key);
    this.placed = left;
    this._render(left);
  }

  _render(list) {
    this.pool.render(list, (el, o) => {
      if (el.textContent !== o.item.text) el.textContent = o.item.text;
      el.dataset.key = o.item.key;
      el.dataset.branch = o.branch;
      el.dataset.kept = String(o.kept);
      el.dataset.at = `${Math.round(o.x)},${Math.round(o.y)}`;
      el.style.transform = `translate(${Math.round(o.x - o.chip.w / 2)}px, ${Math.round(o.y - o.chip.h / 2)}px)`;
    });
  }

  _xy(lngLat) {
    const p = this.map.project(lngLat);
    return [p.x, p.y];
  }
}
